import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useIdentity } from "@/auth/identity";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { tripService } from "@/services/api";
import {
  capitalizeFirst,
  formatLongDate,
  isTomorrowPastCancelCutoff,
} from "@/utils/dateUtils";
import {
  getAllowedWeekdaysUnion,
  getUpcomingAllowedDates,
} from "@/utils/availabilityDays";
import { getAllowedShiftsUnion } from "@/utils/availabilityShifts";
import { buildAvailabilityRows } from "@/utils/buildAvailabilityRows";
import { getDriverOperations, isMultiOperation, operationNamesOf } from "@/utils/operations";
import {
  buildExistingByDate,
  buildRouteShiftsByDate,
  getDayBlock,
} from "@/utils/availabilityBlocking";
import { Shift, Vehicle } from "@/types";
import type { DriverAvailability } from "@/types/availability";
import { readAvailabilitiesResponse } from "@/utils/groupAvailabilities";
import { useDriver } from "@/context/DriverContext";
import BottomNavigation from "@/components/BottomNavigation";
import { CutoffDialog } from "@/components/availability/CutoffDialog";
import { ShiftPicker } from "@/components/availability/ShiftPicker";
import { useShiftDisplay } from "@/hooks/useShiftDisplay";
import { SvcList } from "@/components/availability/SvcList";
import { DayPicker } from "@/components/availability/DayPicker";
import { VehiclePicker } from "@/components/availability/VehiclePicker";
import { OperationSelector } from "@/components/availability/OperationSelector";

const ReportAvailability = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getAccessTokenSilently } = useIdentity();
  const { userScope, todayShipments, tomorrowShipments } = useDriver();
  const shiftDisplay = useShiftDisplay();

  // fecha → turnos con ruta (hoy y mañana)
  const routeShiftsByDate = useMemo(
    () => buildRouteShiftsByDate([...todayShipments, ...tomorrowShipments]),
    [todayShipments, tomorrowShipments]
  );

  // Verificar si estamos en modo edición
  const editMode = location.state?.editMode || false;
  const availabilityToEdit = location.state?.availability || null;

  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedShifts, setSelectedShifts] = useState<Shift[]>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<number[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingByDate, setExistingByDate] = useState<Map<string, Set<number>>>(
    new Map()
  );
  const [selectedOperationIds, setSelectedOperationIds] = useState<number[]>([]);
  const [showCutoffDialog, setShowCutoffDialog] = useState(false);

  const availableSvcs = useMemo(() => userScope?.svcs ?? [], [userScope?.svcs]);

  const ops = useMemo(
    () => getDriverOperations(userScope ?? { operations: [], svcs: [] }),
    [userScope]
  );
  const multiOp = isMultiOperation(ops);
  const operationNames = useMemo(() => operationNamesOf(ops), [ops]);
  // En edición la operación del grupo es fija; con una sola operación, es esa.
  // Si el state no trae operationId (tab vieja), caer a la del SVC de la
  // primera fila del grupo.
  const editOperationId: number | undefined = editMode
    ? (availabilityToEdit?.operationId ?? availabilityToEdit?.availabilities?.[0]?.svc?.operationId)
    : undefined;
  const effectiveOperationIds = useMemo(() => {
    if (editOperationId !== undefined) return [editOperationId];
    return multiOp ? selectedOperationIds : ops.map((o) => o.id);
  }, [editOperationId, multiOp, selectedOperationIds, ops]);
  const activeSvcs = useMemo(
    () => availableSvcs.filter((s) => effectiveOperationIds.includes(s.operationId)),
    [availableSvcs, effectiveOperationIds]
  );
  // Mientras no haya operación marcada, días y turnos se muestran con todos
  // los SVCs para que la pantalla no quede vacía; el fan-out usa activeSvcs.
  const displaySvcs = activeSvcs.length > 0 ? activeSvcs : availableSvcs;

  // Operaciones de los SVCs a la vista: de ellas sale el nombre de los turnos.
  const displayOperationIds = useMemo(
    () => [...new Set(displaySvcs.map((s) => s.operationId))],
    [displaySvcs]
  );

  // Turnos ofrecidos: unión de availabilityShifts de los SVCs relevantes
  // (sin declaración en ningún SVC, todos los turnos como antes).
  const allowedShifts = useMemo(
    () => getAllowedShiftsUnion(displaySvcs),
    [displaySvcs]
  );

  // Bloqueo del día: ruta ya asignada, disponibilidad existente para una
  // operación marcada, o ruta parcial (habilitado con nota, turno salteado).
  const dayBlockOf = useCallback(
    (date: string) =>
      getDayBlock({
        date,
        operationIds: effectiveOperationIds,
        operationNames,
        multiOp,
        shiftsToConsider: selectedShifts.length > 0 ? selectedShifts : allowedShifts,
        existingByDate,
        routeShiftsByDate,
        shiftLabel: shiftDisplay.forOperations,
      }),
    [
      shiftDisplay,
      effectiveOperationIds,
      operationNames,
      multiOp,
      selectedShifts,
      allowedShifts,
      existingByDate,
      routeShiftsByDate,
    ]
  );

  // Si el scope se refetchea y un turno seleccionado deja de estar permitido,
  // quitarlo de la selección. También depende de selectedShifts para podar lo
  // que siembra la edición (el efecto de pre-carga corre después de este): el
  // updater devuelve prev cuando no hay nada que podar, así que no loopea.
  useEffect(() => {
    setSelectedShifts((prev) => {
      const next = prev.filter((s) => allowedShifts.includes(s));
      return next.length === prev.length ? prev : next;
    });
  }, [allowedShifts, selectedShifts]);

  // Un día marcado puede bloquearse después (turno con ruta, operación con filas ese día):
  // sacarlo de la selección, porque su checkbox queda disabled y no se puede desmarcar a mano.
  useEffect(() => {
    if (editMode) return;
    setSelectedDays((prev) => {
      const next = prev.filter((d) => !dayBlockOf(d).disabled);
      return next.length === prev.length ? prev : next;
    });
  }, [editMode, dayBlockOf]);

  // Ventana de días: próximos 7 días de calendario, mostrando solo los weekdays
  // permitidos por la unión de los SVCs relevantes (con Lun-Sáb reproduce lo anterior).
  const getWeekDays = () => {
    const union = getAllowedWeekdaysUnion(displaySvcs);
    const dates = getUpcomingAllowedDates(union, new Date(), 7);

    return dates.map((dateString) => {
      const block = dayBlockOf(dateString);
      return {
        date: dateString,
        label: capitalizeFirst(formatLongDate(dateString)),
        disabled: block.disabled,
        reason: block.reason,
        hint: block.hint,
      };
    });
  };

  const weekDays = getWeekDays();

  // Cargar vehículos del conductor
  useEffect(() => {
    const loadVehicles = async () => {
      try {
        const response = await tripService.getVehicles();
        setVehicles(response.data.data || []);
      } catch (error) {
        console.error("Error loading vehicles:", error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los vehículos",
          variant: "destructive",
        });
      }
    };

    loadVehicles();
  }, []);

  // Cargar disponibilidades existentes solo en modo creación
  useEffect(() => {
    const loadExistingAvailabilities = async () => {
      if (editMode) {
        // En modo edición, no necesitamos cargar disponibilidades existentes
        return;
      }

      try {
        const response = await tripService.getDriverAvailability();

        const availabilitiesData: DriverAvailability[] =
          readAvailabilitiesResponse(response);

        setExistingByDate(buildExistingByDate(availabilitiesData));
      } catch (error) {
        console.error("Error loading existing availabilities:", error);
        // No mostrar error al usuario, simplemente no deshabilitar días
      }
    };

    if (user?.sub) {
      loadExistingAvailabilities();
    }
  }, [editMode, user?.sub]);

  // Pre-cargar datos cuando estamos en modo edición
  useEffect(() => {
    if (editMode && availabilityToEdit) {
      setSelectedDays([availabilityToEdit.date]);
      // En modo edición, no pre-cargamos SVCs ya que ahora se usan todos automáticamente

      // Manejar tanto el formato antiguo (shift único) como el nuevo (shifts array)
      if (
        availabilityToEdit.shifts &&
        Array.isArray(availabilityToEdit.shifts)
      ) {
        setSelectedShifts(availabilityToEdit.shifts);
      } else if (availabilityToEdit.shift) {
        setSelectedShifts([availabilityToEdit.shift]);
      } else {
        setSelectedShifts([Shift.AM]);
      }

      // Manejar vehículos si están disponibles
      if (
        availabilityToEdit.vehicles &&
        Array.isArray(availabilityToEdit.vehicles)
      ) {
        setSelectedVehicles(availabilityToEdit.vehicles);
      }
    }
  }, [editMode, availabilityToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedDays.length === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos un día",
        variant: "destructive",
      });
      return;
    }

    if (multiOp && !editMode && selectedOperationIds.length === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos una operación",
        variant: "destructive",
      });
      return;
    }

    if (activeSvcs.length === 0) {
      toast({
        title: "Error",
        description: "No hay zonas asignadas. Contacte al administrador.",
        variant: "destructive",
      });
      return;
    }

    if (selectedShifts.length === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos un turno",
        variant: "destructive",
      });
      return;
    }

    if (!editMode && selectedVehicles.length === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos un vehículo",
        variant: "destructive",
      });
      return;
    }

    // Editar la disponibilidad de mañana a partir de las 18:00 está bloqueado:
    // mostrar cartel y no enviar (el backend también lo rechaza con 403)
    if (
      editMode &&
      availabilityToEdit?.date &&
      isTomorrowPastCancelCutoff(availabilityToEdit.date)
    ) {
      setShowCutoffDialog(true);
      return;
    }

    performSubmit();
  };

  const performSubmit = async () => {
    setIsSubmitting(true);

    try {
      await getAccessTokenSilently();

      if (editMode && availabilityToEdit) {
        // Modo edición: eliminar existentes y crear nuevas
        const driverId = user?.sub; // Auth0 user ID

        // 1. Construir las nuevas disponibilidades con las combinaciones seleccionadas.
        // El backend marcará automáticamente isFavorite = true para el favoriteSvcId.
        // Solo se crea disponibilidad para el SVC si el día y el turno están
        // habilitados en ESE SVC.
        const availabilities = buildAvailabilityRows({
          days: selectedDays,
          svcs: activeSvcs,
          shifts: selectedShifts,
          vehicleIds: selectedVehicles,
          driverId: driverId!,
          skipShiftsByDate: new Map(
            selectedDays.map((d) => [d, dayBlockOf(d).skipShifts])
          ),
        });

        // Un turno huérfano (de un SVC que dejó de operarlo) puede quedar
        // seleccionado; sin este corte se borrarían las filas existentes y
        // después se intentaría un POST vacío.
        if (availabilities.length === 0) {
          toast({
            title: "Sin combinaciones válidas",
            description: "Ningún SVC opera los días y turnos elegidos.",
            variant: "destructive",
          });
          return;
        }

        // 2. Eliminar todas las disponibilidades existentes del día (una petición → notificación admin única)
        const idsToRemove =
          availabilityToEdit.availabilities?.map((a) => a.id) ?? [];
        if (idsToRemove.length > 0) {
          await tripService.deleteAvailabilitiesBulk(idsToRemove);
        }

        // 3. Crear las nuevas disponibilidades
        await tripService.reportAvailability(availabilities);

        toast({
          title: "Éxito",
          description: "Disponibilidad actualizada correctamente",
        });

        navigate("/my-availabilities");
      } else {
        // Modo creación: crear nuevas disponibilidades
        const driverId = user?.sub; // Auth0 user ID

        // El backend marcará automáticamente isFavorite = true para el favoriteSvcId.
        // Solo se crea disponibilidad para el SVC si el día y el turno están
        // habilitados en ESE SVC.
        const availabilities = buildAvailabilityRows({
          days: selectedDays,
          svcs: activeSvcs,
          shifts: selectedShifts,
          vehicleIds: selectedVehicles,
          driverId: driverId!,
          skipShiftsByDate: new Map(
            selectedDays.map((d) => [d, dayBlockOf(d).skipShifts])
          ),
        });

        if (availabilities.length === 0) {
          toast({
            title: "Sin combinaciones válidas",
            description: "Ningún SVC opera los días y turnos elegidos.",
            variant: "destructive",
          });
          return;
        }

        // Una sola llamada con el array de availabilities
        await tripService.reportAvailability(availabilities);

        toast({
          title: "Éxito",
          description: `Disponibilidad reportada para ${selectedDays.length} día(s)`,
        });

        navigate("/");
      }
    } catch (error: any) {
      let errorMessage = "No se pudo reportar la disponibilidad";

      if (error.response?.status === 400) {
        const backendMessage = error.response?.data?.message;
        if (backendMessage === "Expected object, received number") {
          errorMessage =
            "Error de configuración: ID de disponibilidad inválido";
        } else {
          errorMessage = `Error de validación: ${
            backendMessage || "Datos incorrectos"
          }`;
        }
      } else if (error.response?.status === 409) {
        // Error de duplicación - mostrar mensaje específico del backend
        const backendMessage = error.response?.data?.message;
        if (backendMessage) {
          // Remover el driverId del mensaje para hacerlo más limpio
          errorMessage = backendMessage.replace(/auth0\|[a-zA-Z0-9]+/g, "");
        } else {
          errorMessage =
            "Ya existe una disponibilidad para esta fecha, zona y turno";
        }
      } else if (error.response?.status === 403) {
        // Error de restricción de tiempo
        const backendMessage = error.response?.data?.message;
        if (backendMessage) {
          errorMessage = backendMessage;
        } else {
          errorMessage =
            "No se puede editar la disponibilidad del día actual";
        }
      } else if (error.response?.status === 401) {
        errorMessage = "Sesión expirada. Por favor, inicie sesión nuevamente.";
      } else if (error.response?.status >= 500) {
        errorMessage = "Error del servidor. Intente más tarde.";
      } else if (!navigator.onLine) {
        errorMessage = "Sin conexión a internet. Verifique su conexión.";
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDayToggle = (date: string) => {
    // No permitir seleccionar fechas bloqueadas (ruta u operación ya cargada)
    if (dayBlockOf(date).disabled) {
      return;
    }

    setSelectedDays((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  };


  const handleShiftToggle = (shift: Shift) => {
    setSelectedShifts((prev) =>
      prev.includes(shift) ? prev.filter((s) => s !== shift) : [...prev, shift]
    );
  };

  const handleVehicleToggle = (vehicleId: number) => {
    setSelectedVehicles((prev) =>
      prev.includes(vehicleId)
        ? prev.filter((v) => v !== vehicleId)
        : [...prev, vehicleId]
    );
  };

  const handleOperationToggle = (operationId: number) => {
    setSelectedOperationIds((prev) =>
      prev.includes(operationId)
        ? prev.filter((id) => id !== operationId)
        : [...prev, operationId]
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-16">
      {/* Header */}
      <header className="bg-primary text-white p-4 flex items-center">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => !isSubmitting && navigate(-1)}
          className="text-white mr-2"
          disabled={isSubmitting}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold">
            {editMode ? "Editar Disponibilidad" : "Informar Disponibilidad"}
          </h1>
        </div>
      </header>

      {/* Form */}
      <main className="container mx-auto p-4">
        <form onSubmit={handleSubmit} className="space-y-6">
          <fieldset disabled={isSubmitting} className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            {/* Selección de operación (solo choferes con SVCs en más de una) */}
            {multiOp && (
              <OperationSelector
                operations={ops}
                selected={selectedOperationIds}
                onToggle={handleOperationToggle}
                locked={
                  editMode
                    ? {
                        name:
                          operationNames.get(editOperationId ?? -1) ??
                          `Operación ${editOperationId}`,
                      }
                    : undefined
                }
              />
            )}

            {/* Selección de días */}
            <DayPicker
              days={weekDays}
              selected={selectedDays}
              onToggle={handleDayToggle}
              editDateLabel={
                editMode
                  ? availabilityToEdit?.date
                    ? capitalizeFirst(formatLongDate(availabilityToEdit.date))
                    : ""
                  : undefined
              }
              editHint={
                editMode && availabilityToEdit?.date
                  ? dayBlockOf(availabilityToEdit.date).hint
                  : undefined
              }
            />

            {/* Información de zonas SVC */}
            <SvcList svcs={displaySvcs} />

            {/* Selección de turnos */}
            <ShiftPicker
              shifts={allowedShifts}
              operationIds={displayOperationIds}
              selected={selectedShifts}
              onToggle={(s) => handleShiftToggle(s as Shift)}
            />

            {/* Selección de vehículos */}
            <VehiclePicker
              vehicles={vehicles}
              selected={selectedVehicles}
              onToggle={handleVehicleToggle}
            />
          </div>

          <Button
            type="submit"
            className="w-full py-6"
            variant="outline"
            disabled={
              isSubmitting ||
              selectedDays.length === 0 ||
              (multiOp && !editMode && selectedOperationIds.length === 0) ||
              activeSvcs.length === 0 ||
              selectedShifts.length === 0 ||
              (!editMode && selectedVehicles.length === 0)
            }
          >
            {isSubmitting
              ? "Procesando..."
              : editMode
              ? "Actualizar Disponibilidad"
              : "Informar Disponibilidad"}
          </Button>
          </fieldset>
        </form>
      </main>

      {/* Cutoff dialog: baja bloqueada después de las 18:00 del día anterior */}
      <CutoffDialog open={showCutoffDialog} onOpenChange={setShowCutoffDialog} />

      <BottomNavigation />
    </div>
  );
};

export default ReportAvailability;
